import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge, Button, ConfidenceBar } from "@hp/ui";
import { ClaimStatusPill } from "../ClaimStatusPill";
import type { ClaimCandidateStatus } from "@hp/types";
import { listClaimCandidates } from "../../data/index";
import { ClaimActionButtons } from "../ClaimActionButtons";
import { ErrorState, LoadingState } from "../Feedback";
import {
  formatDate,
  humanizeForum,
  humanizeIssueTag,
} from "../../lib/workbenchFormat";
import { ClaimGroupMemberRows } from "./ClaimGroupMemberRows";
import { ClaimGroupValueDisplay } from "./ClaimGroupValueDisplay";
import {
  groupClaimCandidates,
  groupHasMixedStatus,
  type ClaimCandidateGroup,
} from "../../lib/claimCandidateGroups";
import type { ClaimActionPreset } from "../../lib/claimWorkflowActions";

const PAGE_SIZE = 10;

export interface ClaimCandidatesGroupedTableProps {
  statusFilter?: ClaimCandidateStatus[];
  issueTag?: string;
  search?: string;
  showActions?: boolean;
  actionPreset?: ClaimActionPreset;
  detailPath: (claimId: string) => string;
  emptyMessage: string;
  /** Only claims on "Ledger ready" contracts - matches the Overview's Potential claims card. */
  ledgerReadyOnly?: boolean;
}

export function ClaimCandidatesGroupedTable({
  statusFilter,
  issueTag,
  search,
  showActions = false,
  actionPreset = "review",
  detailPath,
  emptyMessage,
  ledgerReadyOnly = false,
}: ClaimCandidatesGroupedTableProps) {
  const navigate = useNavigate();
  const [offset, setOffset] = React.useState(0);
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    setOffset(0);
    setExpandedKeys(new Set());
  }, [statusFilter, issueTag, search]);

  const queryParams = React.useMemo(
    () => ({
      status: statusFilter,
      issue_tag: issueTag,
      search: search || undefined,
      ledger_ready_only: ledgerReadyOnly || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [statusFilter, issueTag, search, offset, ledgerReadyOnly],
  );

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["claim-candidates", queryParams],
    queryFn: ({ signal }) => listClaimCandidates(queryParams, signal),
    placeholderData: (previous) => previous,
  });

  const groupedRows = React.useMemo(
    () => (data ? groupClaimCandidates(data.items) : []),
    [data],
  );

  function toggleExpanded(key: string) {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Hide the Recoverable value column entirely when every row on this page has nothing to show
  // (e.g. a page of system-rejected claims with no quantified amount) rather than a blank column.
  const anyRowHasValue = groupedRows.some((row) => row.valueHigh > 0 || row.principalHigh > 0 || row.carryingHigh > 0);

  const rawTotal = data?.total ?? 0;
  const groupTotal = groupedRows.length;
  const dedupedCount = groupedRows.reduce((sum, row) => sum + row.memberCount, 0);
  const from = groupTotal === 0 ? 0 : 1;
  const to = groupTotal;

  if (isLoading) return <LoadingState label="Loading claim candidates..." />;
  if (isError) return <ErrorState error={error} label="Could not load claim candidates" />;
  if (!data) return null;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white">
      <div className="table-scroll">
        <table className="table-data text-left text-sm">
          <thead>
            <tr className="border-b border-zinc-200">
              <th className="w-8" aria-hidden />
              <th className="min-w-[10rem] font-medium">Issue</th>
              <th className="col-hide-md min-w-[7rem] font-medium">Forum</th>
              {anyRowHasValue ? <th className="min-w-[8rem] font-medium">Recoverable value</th> : null}
              <th className="col-hide-md min-w-[7rem] font-medium">Confidence</th>
              <th className="min-w-[5.5rem] font-medium">Status</th>
              {showActions ? <th className="min-w-[8rem] font-medium">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {groupedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={(anyRowHasValue ? 6 : 5) + (showActions ? 1 : 0)}
                  className="px-5 py-10 text-center text-zinc-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              groupedRows.map((row) => (
                <ClaimGroupTableRows
                  key={row.key}
                  row={row}
                  expanded={expandedKeys.has(row.key)}
                  onToggleExpand={() => toggleExpanded(row.key)}
                  onOpen={() => navigate(detailPath(row.primary.id))}
                  detailPath={detailPath}
                  showActions={showActions}
                  actionPreset={actionPreset}
                  showValueColumn={anyRowHasValue}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-2 border-t border-zinc-200 px-3 py-2.5 text-xs text-zinc-500 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <span className="min-w-0">
          {isFetching
            ? "Refreshing..."
            : dedupedCount > groupTotal
              ? `Showing ${from}-${to} groups (${dedupedCount} FY tranches)`
              : `Showing ${from}-${to} of ${rawTotal}`}
        </span>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={offset + PAGE_SIZE >= rawTotal}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ClaimGroupTableRowsProps {
  row: ClaimCandidateGroup;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpen: () => void;
  detailPath: (claimId: string) => string;
  showActions: boolean;
  actionPreset: ClaimActionPreset;
  showValueColumn: boolean;
}

function ClaimGroupTableRows({
  row,
  expanded,
  onToggleExpand,
  onOpen,
  detailPath,
  showActions,
  actionPreset,
  showValueColumn,
}: ClaimGroupTableRowsProps) {
  const isGroup = row.memberCount > 1;
  const mixedStatus = groupHasMixedStatus(row.members);
  const colSpan = (showValueColumn ? 6 : 5) + (showActions ? 1 : 0);

  return (
    <>
      <tr
        className="cursor-pointer border-b border-zinc-100 transition-colors hover:bg-zinc-50/80"
        onClick={onOpen}
      >
        <td className="w-8 align-middle">
          {isGroup ? (
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
              aria-expanded={expanded}
              aria-label={expanded ? "Collapse FY tranches" : "Expand FY tranches"}
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpand();
              }}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="inline-block h-7 w-7" aria-hidden />
          )}
        </td>
        <td>
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-medium text-zinc-900">{humanizeIssueTag(row.primary.issue_tag)}</div>
            {mixedStatus ? <Badge tone="warning">Mixed status</Badge> : null}
          </div>
          <div className="mt-1 text-xs text-zinc-400">{formatDate(row.primary.created_at)}</div>
          <div className="mt-0.5 text-xs text-zinc-500 md:hidden">{humanizeForum(row.primary.recommended_forum)}</div>
          {isGroup && !expanded ? (
            <div className="mt-1 text-xs text-zinc-500">
              One claim spanning {row.memberCount} financial years · click chevron to expand amounts
            </div>
          ) : null}
        </td>
        <td className="col-hide-md text-zinc-600">{humanizeForum(row.primary.recommended_forum)}</td>
        {showValueColumn ? (
          <td>
            <ClaimGroupValueDisplay
              rollup={{
                principal_high: row.principalHigh,
                carrying_cost_high: row.carryingHigh,
                total_high: row.valueHigh,
                member_count: row.memberCount,
              }}
              compact
            />
          </td>
        ) : null}
        <td className="col-hide-md">
          <ConfidenceBar value={Math.round(row.primary.confidence_score * 100)} className="min-w-[5rem]" />
        </td>
        <td>
          <ClaimStatusPill
            status={row.primary.status}
            counselValidatedAt={row.primary.counsel_validated_at}
          />
        </td>
        {showActions ? (
          <td className="whitespace-normal sm:whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
            <ClaimActionButtons candidate={row.primary} preset={actionPreset} stopPropagation />
            {isGroup ? (
              <p className="mt-1 max-w-[12rem] text-[0.625rem] leading-snug text-zinc-400">
                This one claim spans {row.memberCount} financial years
              </p>
            ) : null}
          </td>
        ) : null}
      </tr>
      {isGroup && expanded ? (
        <ClaimGroupMemberRows
          members={row.members}
          detailPath={detailPath}
          colSpan={colSpan}
        />
      ) : null}
    </>
  );
}
